import { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    Calendar, ChevronLeft, ChevronRight, Users, Clock, Dog,
    Mail, Phone, Eye, CheckCircle2, XCircle, AlertCircle,
    X, FileText, Trash2, List, LayoutGrid, Plus
} from 'lucide-react';

function getMonthDays(year, month) {
    var firstDay = new Date(year, month, 1).getDay();
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var days = [];
    for (var i = 0; i < firstDay; i++) {
        days.push(null);
    }
    for (var d = 1; d <= daysInMonth; d++) {
        days.push(d);
    }
    return days;
}

function formatDate(year, month, day) {
    var m = String(month + 1).padStart(2, '0');
    var d = String(day).padStart(2, '0');
    return year + '-' + m + '-' + d;
}

var MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
var DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function StatusBadge({ status }) {
    if (status === 'confirmed') return <Badge className="bg-green-100 text-green-700 text-xs">Confirmed</Badge>;
    if (status === 'cancelled') return <Badge className="bg-red-100 text-red-700 text-xs">Cancelled</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>;
}

export default function BookingsCalendar({
    bookings,
    safetyForms,
    slots,
    sessions,
    onOpenBookingDetails,
    onUpdateStatus,
    onDeleteBooking,
    onAddBooking
}) {
    var [currentYear, setCurrentYear] = useState(function() {
        if (bookings && bookings.length > 0) {
            var dates = bookings.map(function(b) { return b.date; }).filter(Boolean).sort();
            if (dates.length > 0) return parseInt(dates[dates.length - 1].split('-')[0]);
        }
        return new Date().getFullYear();
    });
    var [currentMonth, setCurrentMonth] = useState(function() {
        if (bookings && bookings.length > 0) {
            var dates = bookings.map(function(b) { return b.date; }).filter(Boolean).sort();
            if (dates.length > 0) return parseInt(dates[dates.length - 1].split('-')[1]) - 1;
        }
        return new Date().getMonth();
    });
    var [selectedDate, setSelectedDate] = useState(null);
    var [filter, setFilter] = useState('all');
    var [viewMode, setViewMode] = useState('calendar');

    // Build lookup maps
    var bookingsByDate = useMemo(function() {
        var map = {};
        bookings.forEach(function(b) {
            if (!b.date) return;
            if (!map[b.date]) map[b.date] = [];
            map[b.date].push(b);
        });
        return map;
    }, [bookings]);

    var slotsByDate = useMemo(function() {
        var map = {};
        slots.forEach(function(s) {
            if (!s.date) return;
            if (!map[s.date]) map[s.date] = [];
            map[s.date].push(s);
        });
        return map;
    }, [slots]);

    var sessionMap = useMemo(function() {
        var map = {};
        sessions.forEach(function(s) { map[s.id] = s; });
        return map;
    }, [sessions]);

    // Filter bookings
    var filteredBookings = useMemo(function() {
        var today = new Date().toISOString().slice(0, 10);
        return bookings.filter(function(b) {
            if (filter === 'upcoming') return b.date >= today;
            if (filter === 'past') return b.date < today;
            if (filter === 'confirmed') return b.status === 'confirmed';
            if (filter === 'pending') return b.status === 'pending';
            if (filter === 'cancelled') return b.status === 'cancelled';
            return true;
        });
    }, [bookings, filter]);

    var filteredByDate = useMemo(function() {
        var map = {};
        filteredBookings.forEach(function(b) {
            if (!b.date) return;
            if (!map[b.date]) map[b.date] = [];
            map[b.date].push(b);
        });
        return map;
    }, [filteredBookings]);

    function prevMonth() {
        if (currentMonth === 0) { setCurrentYear(currentYear - 1); setCurrentMonth(11); }
        else setCurrentMonth(currentMonth - 1);
        setSelectedDate(null);
    }
    function nextMonth() {
        if (currentMonth === 11) { setCurrentYear(currentYear + 1); setCurrentMonth(0); }
        else setCurrentMonth(currentMonth + 1);
        setSelectedDate(null);
    }

    function getDateBookings(dateStr) {
        return filteredByDate[dateStr] || [];
    }

    function getDateSlots(dateStr) {
        return slotsByDate[dateStr] || [];
    }

    function getTotalCapacity(dateStr) {
        var dateSlots = getDateSlots(dateStr);
        var total = 0;
        dateSlots.forEach(function(s) {
            var sess = sessionMap[s.session_id];
            total += (sess ? sess.max_participants : 4);
        });
        return total;
    }

    function getDateStatus(dateStr) {
        var db = getDateBookings(dateStr);
        if (db.length === 0) return 'empty';
        var hasConfirmed = db.some(function(b) { return b.status === 'confirmed'; });
        var hasPending = db.some(function(b) { return b.status === 'pending'; });
        var hasCancelled = db.some(function(b) { return b.status === 'cancelled'; });
        var totalCap = getTotalCapacity(dateStr);
        if (db.length >= totalCap && totalCap > 0) return 'full';
        if (hasConfirmed) return 'confirmed';
        if (hasPending) return 'pending';
        if (hasCancelled) return 'cancelled';
        return 'partial';
    }

    function getSafetyForm(booking) {
        return safetyForms.find(function(sf) { return sf.cart_id === booking.cart_id; }) || null;
    }

    var days = getMonthDays(currentYear, currentMonth);
    var today = new Date().toISOString().slice(0, 10);

    // Selected date detail
    var selectedBookings = selectedDate ? getDateBookings(selectedDate) : [];
    var selectedSlots = selectedDate ? getDateSlots(selectedDate) : [];

    var filters = [
        { key: 'all', label: 'All' },
        { key: 'upcoming', label: 'Upcoming' },
        { key: 'past', label: 'Past' },
        { key: 'confirmed', label: 'Confirmed' },
        { key: 'pending', label: 'Pending' },
        { key: 'cancelled', label: 'Cancelled' }
    ];

    return (
        <div data-testid="bookings-calendar">
            {/* Summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">{bookings.length}</p>
                    <p className="text-xs text-muted-foreground">Total Bookings</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{bookings.filter(function(b){ return b.status === 'confirmed'; }).length}</p>
                    <p className="text-xs text-green-600">Confirmed</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{bookings.filter(function(b){ return b.status === 'pending'; }).length}</p>
                    <p className="text-xs text-amber-600">Pending</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-700">{bookings.filter(function(b){ return b.status === 'cancelled'; }).length}</p>
                    <p className="text-xs text-red-600">Cancelled</p>
                </div>
            </div>

            {/* Header with toggle and filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <Button
                        variant={viewMode === 'calendar' ? 'default' : 'outline'}
                        size="sm"
                        onClick={function() { setViewMode('calendar'); }}
                        data-testid="view-calendar-btn"
                    >
                        <LayoutGrid className="w-4 h-4 mr-1" /> Calendar
                    </Button>
                    <Button
                        variant={viewMode === 'list' ? 'default' : 'outline'}
                        size="sm"
                        onClick={function() { setViewMode('list'); }}
                        data-testid="view-list-btn"
                    >
                        <List className="w-4 h-4 mr-1" /> List
                    </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {filters.map(function(f) {
                        return (
                            <Button
                                key={f.key}
                                variant={filter === f.key ? 'default' : 'outline'}
                                size="sm"
                                className={'text-xs h-7 px-2.5 ' + (filter === f.key ? 'bg-primary' : '')}
                                onClick={function() { setFilter(f.key); }}
                                data-testid={'filter-' + f.key}
                            >
                                {f.label}
                            </Button>
                        );
                    })}
                </div>
            </div>

            {viewMode === 'calendar' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Calendar grid */}
                    <div className="lg:col-span-2">
                        <Card>
                            <CardContent className="p-3 sm:p-4">
                                {/* Month navigation */}
                                <div className="flex items-center justify-between mb-2">
                                    <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month">
                                        <ChevronLeft className="w-5 h-5" />
                                    </Button>
                                    <h3 className="text-lg font-bold font-serif">
                                        {MONTH_NAMES[currentMonth]} {currentYear}
                                    </h3>
                                    <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month">
                                        <ChevronRight className="w-5 h-5" />
                                    </Button>
                                </div>

                                {/* Quick jump to months with bookings */}
                                {Object.keys(bookingsByDate).length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-3">
                                        {Array.from(new Set(bookings.map(function(b) { return b.date ? b.date.slice(0,7) : null; }).filter(Boolean))).sort().map(function(ym) {
                                            var parts = ym.split('-');
                                            var y = parseInt(parts[0]);
                                            var m = parseInt(parts[1]) - 1;
                                            var isActive = y === currentYear && m === currentMonth;
                                            var count = bookings.filter(function(b) { return b.date && b.date.startsWith(ym); }).length;
                                            return (
                                                <button
                                                    key={ym}
                                                    onClick={function() { setCurrentYear(y); setCurrentMonth(m); setSelectedDate(null); }}
                                                    className={'text-xs px-2 py-1 rounded-full border transition-colors ' + (isActive ? 'bg-primary text-white border-primary' : 'bg-white hover:bg-secondary/50 border-border')}
                                                >
                                                    {MONTH_NAMES[m].slice(0,3)} {y} ({count})
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Day headers */}
                                <div className="grid grid-cols-7 gap-1 mb-1">
                                    {DAY_NAMES.map(function(d) {
                                        return <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>;
                                    })}
                                </div>

                                {/* Days grid */}
                                <div className="grid grid-cols-7 gap-1">
                                    {days.map(function(day, idx) {
                                        if (day === null) {
                                            return <div key={'empty-' + idx} className="aspect-square" />;
                                        }

                                        var dateStr = formatDate(currentYear, currentMonth, day);
                                        var dateBookings = getDateBookings(dateStr);
                                        var status = getDateStatus(dateStr);
                                        var isToday = dateStr === today;
                                        var isSelected = dateStr === selectedDate;
                                        var totalCap = getTotalCapacity(dateStr);
                                        var spacesLeft = totalCap - dateBookings.length;

                                        var bgClass = 'bg-white hover:bg-secondary/50';
                                        if (status === 'confirmed' || status === 'full') bgClass = 'bg-green-50 hover:bg-green-100 border-green-200';
                                        else if (status === 'pending' || status === 'partial') bgClass = 'bg-amber-50 hover:bg-amber-100 border-amber-200';
                                        else if (status === 'cancelled') bgClass = 'bg-red-50 hover:bg-red-100 border-red-200';

                                        if (isSelected) bgClass = 'bg-primary/10 ring-2 ring-primary border-primary';

                                        return (
                                            <button
                                                key={dateStr}
                                                onClick={function() { setSelectedDate(dateStr); }}
                                                className={'relative aspect-square p-0.5 sm:p-1 rounded-lg border text-left transition-all cursor-pointer flex flex-col ' + bgClass + (isToday ? ' ring-1 ring-primary/40' : '')}
                                                data-testid={'cal-day-' + dateStr}
                                            >
                                                <span className={'text-xs sm:text-sm font-medium ' + (isToday ? 'text-primary font-bold' : 'text-foreground')}>
                                                    {day}
                                                </span>
                                                {dateBookings.length > 0 && (
                                                    <div className="flex-1 flex flex-col justify-end">
                                                        <div className="text-[10px] sm:text-xs leading-tight text-muted-foreground">
                                                            <span className="flex items-center gap-0.5">
                                                                <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                                                <span className="font-semibold">{dateBookings.length}</span>
                                                            </span>
                                                        </div>
                                                        {totalCap > 0 && (
                                                            <div className={'text-[9px] sm:text-[10px] leading-tight ' + (spacesLeft <= 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground')}>
                                                                {spacesLeft <= 0 ? 'Full' : spacesLeft + ' left'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-200" /> Confirmed</span>
                                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> Pending</span>
                                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-200" /> Cancelled</span>
                                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border" /> No bookings</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Date detail panel */}
                    <div className="lg:col-span-1">
                        <DateDetailPanel
                            selectedDate={selectedDate}
                            bookings={selectedBookings}
                            slots={selectedSlots}
                            sessionMap={sessionMap}
                            getSafetyForm={getSafetyForm}
                            onOpenBookingDetails={onOpenBookingDetails}
                            onUpdateStatus={onUpdateStatus}
                            onDeleteBooking={onDeleteBooking}
                            onClose={function() { setSelectedDate(null); }}
                            onAddBooking={onAddBooking}
                        />
                    </div>
                </div>
            ) : (
                /* List View */
                <BookingsListView
                    bookings={filteredBookings}
                    safetyForms={safetyForms}
                    onOpenBookingDetails={onOpenBookingDetails}
                    onUpdateStatus={onUpdateStatus}
                    onDeleteBooking={onDeleteBooking}
                />
            )}
        </div>
    );
}

function DateDetailPanel({ selectedDate, bookings, slots, sessionMap, getSafetyForm, onOpenBookingDetails, onUpdateStatus, onDeleteBooking, onClose, onAddBooking }) {
    if (!selectedDate) {
        return (
            <Card className="h-full">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                    <Calendar className="w-12 h-12 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">Click a date to see bookings</p>
                </CardContent>
            </Card>
        );
    }

    var dateObj = new Date(selectedDate + 'T12:00:00');
    var dateLabel = dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    // Group bookings by time slot
    var byTime = {};
    bookings.forEach(function(b) {
        var key = b.time || 'unknown';
        if (!byTime[key]) byTime[key] = [];
        byTime[key].push(b);
    });
    var timeKeys = Object.keys(byTime).sort();

    // Get capacity per slot
    var slotCapacity = {};
    slots.forEach(function(s) {
        var sess = sessionMap[s.session_id];
        slotCapacity[s.time] = sess ? sess.max_participants : 4;
    });

    return (
        <Card className="h-full" data-testid="date-detail-panel">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-bold text-base">{dateLabel}</h3>
                        <p className="text-xs text-muted-foreground">{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex items-center gap-1">
                        {onAddBooking && (
                            <Button variant="outline" size="sm" className="text-xs h-7" onClick={function() { onAddBooking({ date: selectedDate }); }}>
                                <Plus className="w-3 h-3 mr-1" /> Add
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {bookings.length === 0 ? (
                    <div className="text-center py-8">
                        <Calendar className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No bookings on this date</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {timeKeys.map(function(time) {
                            var timeBookings = byTime[time];
                            var cap = slotCapacity[time] || 4;
                            var spacesLeft = cap - timeBookings.length;

                            return (
                                <div key={time} className="border rounded-lg overflow-hidden">
                                    <div className="bg-secondary/50 px-3 py-2 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-primary" />
                                            <span className="font-semibold text-sm">{time}</span>
                                            <span className="text-xs text-muted-foreground">
                                                ({timeBookings[0].session_name})
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs">
                                            <Users className="w-3 h-3" />
                                            <span className="font-medium">{timeBookings.length}/{cap}</span>
                                            {timeBookings.length > cap ? (
                                                <Badge className="bg-red-600 text-white text-[10px] ml-1 animate-pulse">OVERBOOKED</Badge>
                                            ) : spacesLeft <= 0 ? (
                                                <Badge className="bg-red-100 text-red-700 text-[10px] ml-1">Full</Badge>
                                            ) : (
                                                <span className="text-muted-foreground ml-1">({spacesLeft} left)</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="divide-y">
                                        {timeBookings.map(function(booking) {
                                            var sf = getSafetyForm(booking);
                                            return (
                                                <BookingRow
                                                    key={booking.id}
                                                    booking={booking}
                                                    safetyForm={sf}
                                                    onOpenDetails={onOpenBookingDetails}
                                                    onUpdateStatus={onUpdateStatus}
                                                    onDelete={onDeleteBooking}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function BookingRow({ booking, safetyForm, onOpenDetails, onUpdateStatus, onDelete }) {
    var [expanded, setExpanded] = useState(false);

    return (
        <div className="px-3 py-2" data-testid={'booking-row-' + booking.id}>
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={function() { setExpanded(!expanded); }}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <div className={'w-2 h-2 rounded-full flex-shrink-0 ' + (booking.status === 'confirmed' ? 'bg-green-500' : booking.status === 'cancelled' ? 'bg-red-500' : 'bg-amber-500')} />
                    <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                            {safetyForm ? safetyForm.handlerName : 'Unknown'}
                        </p>
                        {safetyForm && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                <Dog className="w-3 h-3 flex-shrink-0" />
                                {safetyForm.dogName} ({safetyForm.dogBreed || 'N/A'}{safetyForm.dogGender ? ', ' + safetyForm.dogGender : ''})
                            </p>
                        )}
                    </div>
                </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <StatusBadge status={booking.status} />
                                            {booking.overbooked && (
                                                <Badge className="bg-red-600 text-white text-[10px] animate-pulse">OVERBOOKED</Badge>
                                            )}
                                            <span className="text-xs font-semibold text-primary">£{booking.price}</span>
                                        </div>
            </div>

            {expanded && (
                <div className="mt-2 pt-2 border-t space-y-2">
                    {/* Contact details */}
                    {safetyForm && (
                        <div className="text-xs space-y-1 text-muted-foreground">
                            {safetyForm.emailAddress && (
                                <p className="flex items-center gap-1.5">
                                    <Mail className="w-3 h-3" />
                                    <a href={'mailto:' + safetyForm.emailAddress} className="hover:text-primary">{safetyForm.emailAddress}</a>
                                </p>
                            )}
                            {safetyForm.emergencyContact && (
                                <p className="flex items-center gap-1.5">
                                    <Phone className="w-3 h-3" /> {safetyForm.emergencyContact}
                                </p>
                            )}
                            {safetyForm.healthConditions && safetyForm.healthConditions !== 'None specified' && (
                                <p className="flex items-center gap-1.5">
                                    <AlertCircle className="w-3 h-3 text-amber-500" /> Health: {safetyForm.healthConditions}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Safety form status */}
                    <div className="flex items-center gap-1.5 text-xs">
                        <FileText className="w-3 h-3" />
                        {safetyForm ? (
                            <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Safety form submitted</span>
                        ) : (
                            <span className="text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> No safety form</span>
                        )}
                    </div>

                    {/* Payment */}
                    <div className="flex items-center gap-1.5 text-xs">
                        {booking.status === 'confirmed' ? (
                            <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Paid</span>
                        ) : booking.status === 'cancelled' ? (
                            <span className="text-red-600 flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancelled</span>
                        ) : (
                            <span className="text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Payment pending</span>
                        )}
                    </div>

                    {/* Quick actions */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={function(e) { e.stopPropagation(); onOpenDetails(booking); }}>
                            <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        {booking.status !== 'confirmed' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-green-600 hover:bg-green-50" onClick={function(e) { e.stopPropagation(); onUpdateStatus(booking.id, 'confirmed'); }}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
                            </Button>
                        )}
                        {booking.status !== 'cancelled' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-amber-600 hover:bg-amber-50" onClick={function(e) { e.stopPropagation(); onUpdateStatus(booking.id, 'cancelled'); }}>
                                <XCircle className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                        )}
                        {safetyForm && safetyForm.emailAddress && (
                            <a href={'mailto:' + safetyForm.emailAddress} onClick={function(e) { e.stopPropagation(); }}>
                                <Button size="sm" variant="outline" className="h-7 text-xs px-2">
                                    <Mail className="w-3 h-3 mr-1" /> Email
                                </Button>
                            </a>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-red-600 hover:bg-red-50" onClick={function(e) { e.stopPropagation(); onDelete(booking.id, booking); }}>
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function BookingsListView({ bookings, safetyForms, onOpenBookingDetails, onUpdateStatus, onDeleteBooking }) {
    if (bookings.length === 0) {
        return <p className="text-center text-muted-foreground py-8">No bookings match your filter</p>;
    }

    return (
        <div className="space-y-3" data-testid="bookings-list-view">
            {bookings.map(function(b) {
                var safetyForm = safetyForms.find(function(sf) { return sf.cart_id === b.cart_id; });
                return (
                    <div
                        key={b.id}
                        className="p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors border border-transparent hover:border-primary/20"
                        onClick={function() { onOpenBookingDetails(b); }}
                        data-testid={'booking-' + b.id}
                    >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className={'w-2 h-8 rounded-full flex-shrink-0 ' + (b.status === 'confirmed' ? 'bg-green-500' : b.status === 'cancelled' ? 'bg-red-500' : 'bg-amber-500')} />
                                <div>
                                    <p className="font-semibold">{b.session_name}</p>
                                    <p className="text-sm text-muted-foreground">{b.date} at {b.time}</p>
                                    {safetyForm && (
                                        <p className="text-sm text-primary flex items-center gap-1">
                                            <Dog className="w-3 h-3" />
                                            {safetyForm.handlerName} & {safetyForm.dogName}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-lg font-semibold text-primary">£{b.price}</span>
                                <StatusBadge status={b.status} />
                                {b.overbooked && <Badge className="bg-red-600 text-white text-xs animate-pulse">OVERBOOKED</Badge>}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
